import * as CSS from "csstype";
import {
  Box,
  Flex,
  VStack,
  Text,
  Button,
  Divider,
  HStack,
} from "@chakra-ui/react";
import { MessageFragment, MessageThreadFragment } from "gql/graphql";
import React, { memo, useCallback, useMemo } from "react";
import {
  MESSAGE_NOT_SENT_OPACITY,
  MESSAGE_STATE,
  MessageSubType,
} from "modules/utils/Constants";
import useMessageThreadTranslation from "modules/hooks/use-message-thread-translation";
import MessageGifViewer from "../messages/message-gif-viewer";
import QuotedMessages from "../messages/quoted-messages";
import colors from "app/colors";
import { NotesIcon, TranslateIcon } from "modules/icons";
import MessageText from "../messages/message-text";
import { useOverlay } from "modules/providers/overlay-provider";
import { MODAL_TYPE } from "modules/providers/overlay-provider/overlay-provider";
import MessageFiles from "../messages/message-files";
import { isMessageEmpty } from "modules/utils/Functions";
import useRemovedFromThread from "modules/hooks/use-removed-from-thread";
import useTask from "modules/hooks/use-task";
import TaskMessagePopover from "./task-message-popover";

interface IProps {
  messageThread: MessageThreadFragment;
  message: MessageFragment;
  messageState: MESSAGE_STATE;
}

const TaskMessage: React.FC<IProps> = ({
  messageThread,
  message,
  messageState,
}) => {
  const { openModal } = useOverlay();
  const { isMeRemoved } = useRemovedFromThread(messageThread);
  const { isTaskClosed, isCreatorOrAssignee } = useTask({
    messageThread,
  });

  const openEditMessageModal = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      e.preventDefault();
      openModal({
        type: MODAL_TYPE.EDIT_MESSAGE,
        id: message.id,
      });
    },
    [message.id, openModal],
  );
  const { isViewingTranslatedMessage } = useMessageThreadTranslation(
    message,
    messageThread,
  );

  const messageFontStyle = useMemo((): CSS.Property.FontStyle => {
    if (message.subType === MessageSubType.OUT_OF_OFFICE) {
      return "italic";
    }
    return "normal";
  }, [message.subType]);

  const gifList = message.gifList;

  const allowModify = !isTaskClosed && !isMeRemoved && isCreatorOrAssignee;

  if (isMessageEmpty(message) && allowModify) {
    return (
      <VStack w="full" alignItems="flex-start" py={2}>
        <Button
          fontSize={12}
          fontWeight={600}
          size="xxs"
          variant="ghost"
          color={colors.blue}
          onClick={openEditMessageModal}
          leftIcon={<NotesIcon fill={colors.blue} boxSize={4} />}
          iconSpacing={1}
        >
          Add Description & Files
        </Button>
        <Divider borderColor={colors.grayPrimary} />
      </VStack>
    );
  }

  return (
    <VStack w="full" spacing={1} alignItems="flex-start" pb={2}>
      <Flex w="full" align="center" pb={2}>
        <Text
          color={colors.gray50}
          fontSize={12}
          fontWeight={500}
          whiteSpace="nowrap"
        >
          Description & Files
        </Text>
        <TaskMessagePopover messageThread={messageThread} message={message} />
      </Flex>
      <QuotedMessages messageThread={messageThread} message={message} />
      <HStack w="full" alignItems="flex-start">
        {isViewingTranslatedMessage && (
          <TranslateIcon boxSize={3} fill={colors.grayMain} mt={1} />
        )}
        <Box
          color={colors.grayMain}
          fontWeight={500}
          fontSize={14}
          fontStyle={messageFontStyle}
          sx={{ margin: "0 !important" }}
        >
          <MessageText messageThread={messageThread} message={message} />
        </Box>
      </HStack>
      {gifList.length > 0 && (
        <Box
          pb="8px"
          opacity={messageState === "not-sent" ? MESSAGE_NOT_SENT_OPACITY : 1}
        >
          {gifList.map((gif, index) => (
            <Box key={index} pl={isViewingTranslatedMessage ? 3 : 0} pb={1}>
              <MessageGifViewer
                messageThread={messageThread}
                message={message}
                gif={gif}
              />
            </Box>
          ))}
        </Box>
      )}
      <MessageFiles
        messageThread={messageThread}
        message={message}
        messageState={messageState}
        fileThumbSize={[125, 125]}
      />
      <Divider py={2} borderColor={colors.grayPrimary} />
    </VStack>
  );
};

export default memo(TaskMessage);
